<template>
  <div :class="['si-wrap', {'is-91xj': HNewGameUIConfig.newGameUIType === 3}]">
    <!-- 顶部提示行：后端已替换 {} 占位符 -->
    <div v-if="topTips" class="si-tip-row" v-html="topTips"></div>

    <!-- 签到格子 -->
    <div class="si-grid">
      <div
          v-for="(aw, idx) in awards"
          :key="aw.id ?? idx"
          :class="['si-cell', cellState(aw, idx)]"
          :style="{ backgroundImage: `url(${cellBg(aw, idx)})` }"
          @click="onCellClick(aw, idx)"
      >
        <div class="si-cell-img">
          <img :src="coinIcon(aw, idx)" class="si-coin" alt=""/>
        </div>
        <div class="si-cell-name">{{ formatInt(aw.award_value) || 1 }}元</div>
        <div :class="['si-cell-tag', cellTagClass(aw, idx)]">{{ cellTagText(aw) }}</div>
        <img v-if="cellState(aw, idx) === 'si-cell-claimed'" :src="successIcon" class="si-cell-success" alt="已领取"/>
      </div>
    </div>

    <!-- 活动规则 -->
    <div v-if="ruleText" class="si-rules">
      <div class="si-rules-title">活动规则:</div>
      <div v-if="ruleIsHtml" v-html="ruleText" class="si-rules-body"></div>
      <div v-else class="si-rules-body si-rules-body-text">{{ ruleText }}</div>
    </div>
  </div>
</template>

<script setup>
import {computed} from 'vue';
import {showToast} from "vant";
import {useHHttp} from "@/hooks/useHHttp.js";
import qd123 from "@/assets/icons/activity/qd_123.png";
import qd4567 from "@/assets/icons/activity/qd_4567.png";
import qdSpecial from "@/assets/icons/activity/qd_special.png";
import bgDefault from "@/assets/icons/activity/qd_bg_default.png";
import bgNext from "@/assets/icons/activity/qd_bg_next.png";
import bgSpecial from "@/assets/icons/activity/qd_bg_specail.png";
import successIcon from "@/assets/icons/activity/qd_success.png";
import HNewGameUIConfig from "@/components/utils/HNewGameUIConfig.js";

const props = defineProps({
  activityId: [Number, String],
  detail: Object,
});
const emit = defineEmits(['refresh']);

const http = useHHttp();

// /qp/app/activity/detail 在 act_type=2 时已内联签到字段，前端不再调 /qp/app/sign-in/info
const awards = computed(() => {
  const list = props.detail?.awards ?? [];
  return [...list].sort((a, b) => (b.seq ?? 0) - (a.seq ?? 0));
});
const cycleDay = computed(() => props.detail?.cycle_day ?? 0);
const consecutiveDays = computed(() => props.detail?.consecutive_days ?? 0);
const signedToday = computed(() => !!props.detail?.signed_today);
const canSign = computed(() => !!props.detail?.can_sign);
const topTips = computed(() => props.detail?.top_tips ?? '');

const ruleText = computed(() => props.detail?.activity?.desc_text ?? '');
const ruleIsHtml = computed(() => /<[a-z][\s\S]*>/i.test(ruleText.value));

function formatInt(fen) {
  return Math.floor((fen ?? 0) / 100);
}

// awards[].day 是签到专属字段(1-7 = 周期天数, 8 = 连续 7 天后额外奖)；旧接口/缺字段时回退到下标
function dayOf(aw, idx) {
  return aw?.day || (idx + 1);
}

function isBonusCell(aw, idx) {
  return dayOf(aw, idx) >= 8;
}

function cellState(aw, idx) {
  if (isBonusCell(aw, idx)) {
    if (aw?.claimed) return 'si-cell-claimed';
    // 额外奖：仅在后端明确 can_claim=true 时高亮，避免没满 7 天就误点
    if (aw?.can_claim) return 'si-cell-today';
    return 'si-cell-final';
  }
  const day = dayOf(aw, idx);
  if (day <= cycleDay.value) return 'si-cell-claimed';
  // 高亮"今天应签"的格子；canSign=false（规则未满足）只影响能否点击，不影响高亮
  if (!signedToday.value && day === cycleDay.value + 1) return 'si-cell-today';
  return 'si-cell-pending';
}

function cellTagText(aw) {
  return aw?.title ?? '';
}

function cellTagClass(aw, idx) {
  const state = cellState(aw, idx);
  if (state === 'si-cell-today') return 'si-cell-tag-today';
  if (isBonusCell(aw, idx)) return 'si-cell-tag-final';
  return 'si-cell-tag-default';
}

function coinIcon(aw, idx) {
  if (isBonusCell(aw, idx)) return qdSpecial;
  const day = dayOf(aw, idx);
  return day <= 3 ? qd123 : qd4567;
}

function cellBg(aw, idx) {
  const state = cellState(aw, idx);
  if (isBonusCell(aw, idx) && state !== 'si-cell-today') return bgSpecial;
  if (state === 'si-cell-today') return bgNext;
  return bgDefault;
}

function onCellClick(/* aw, idx */) {
  // 所有格子都允许点击；前端不再判断 today / canSign，由服务器返回相应提示
  doSign();
}

function doSign() {
  http.request("/qp/app/sign-in/claim", (data) => {
    const amount = (data?.award_amount ?? 0) / 100;
    showToast(amount > 0 ? `签到成功 +${amount.toFixed(2)} 元` : '签到成功');
    emit('refresh');
  }, {params: {activity_id: props.activityId}});
}
</script>

<style scoped>
.si-wrap {
  padding: 10px;
  width: 100%;
  box-sizing: border-box;
  background: #fff;
  border-radius: 10px;
}

.si-wrap * {
  box-sizing: border-box;
}

.si-loading {
  text-align: center;
  color: #999;
  padding: 40px 0;
}

/* 顶部提示行 */
.si-tip-row {
  font-size: 12px;
  color: var(--ct-main-color);
  padding: 6px 6px 10px;
  font-weight: normal;
}

.si-tip-row b {
  font-weight: bold;
}

/* ===== 4×2 格子 ===== */
.si-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
  width: 100%;
  background: #fff;
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.si-cell {
  min-width: 0;
  padding: 8px 3px 6px;
  text-align: center;
  position: relative;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center;
  cursor: default;
  overflow: hidden;
}

.si-cell-img {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 32px;
}

.si-coin {
  max-width: 70%;
  max-height: 100%;
  width: auto;
  height: auto;
  display: block;
}

.si-cell-name {
  font-size: 11px;
  color: #000;
  margin: 4px 0 6px;
}

.si-cell-tag {
  font-size: 11px;
  font-weight: bold;
  border-radius: 6px;
  padding: 3px 0;
  margin: 0 6px;
}

.si-cell-tag-default {
  background: #f5e3b8;
  color: #8b5a1f;
}

.si-cell-tag-today {
  background: #f5c247;
  color: #6a3d00;
}

.si-cell-tag-final {
  background: #d94c4c;
  color: #fff;
}

/* 已领取：灰黑遮罩 + 印章 */
.si-cell-claimed::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  pointer-events: none;
  z-index: 2;
}

.si-cell-success {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70%;
  height: auto;
  pointer-events: none;
  z-index: 1;
}

/* 今日：可点击 */
.si-cell-today {
  cursor: pointer;
}

/* ===== 活动规则 ===== */
.si-rules {
  margin-top: 16px;
  padding: 4px;
}

.si-rules-title {
  color: var(--ct-main-color);
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 8px;
}

.si-rules-body {
  font-size: 13px;
  color: #333;
  line-height: 1.7;
}

.si-rules-body-text {
  white-space: pre-line;
}

.si-rules-body :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
}

/* ===== 91xj 暗色覆盖 ===== */
.si-wrap.is-91xj {
  background: #1c1c1c;
}

.is-91xj .si-grid {
  background: #222;
}

.is-91xj .si-rules-body {
  color: #ccc;
}

/* 91xj 下 qd_bg_next（today）/ qd_bg_specail（final）背景的金额改用主题色 */
.is-91xj .si-cell.si-cell-today .si-cell-name,
.is-91xj .si-cell.si-cell-final .si-cell-name {
  color: var(--ct-main-color);
  font-weight: bold;
}
</style>
